package com.klantenstop.web.components.table

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.klantenstop.web.styles.TableStyles
import com.varabyte.kobweb.compose.css.BorderCollapse
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.css.VerticalAlign
import com.varabyte.kobweb.compose.css.borderTop
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderCollapse
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.resize
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.userSelect
import com.varabyte.kobweb.compose.ui.modifiers.verticalAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.materialkobweb.styles.MaterialColorVars
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/**
 * Generic reusable data table with sorting and optional row expansion
 *
 * @param T The type of items displayed in the table
 * @param items List of items to display
 * @param columns Column definitions
 * @param emptyMessage Message to show when list is empty
 * @param expandedRowKey Optional key for the currently expanded row (null if no expansion)
 * @param getRowKey Function to get unique key for an item (required for row expansion)
 * @param onRowClick Callback when a row is clicked (for expansion support)
 * @param expandedContent Composable to render in the expanded row (receives the item)
 * @param groupKeyOf Optional function to derive a group label for an item. When provided, a
 *   sub-header row is rendered above each group (order controlled by [groupOrder], falling back
 *   to first-appearance order in [items]). Sorting still applies, but only within each group.
 * @param groupOrder Optional explicit ordering of group labels (as returned by [groupKeyOf])
 */
@Composable
fun <T> DataTable(
    items: List<T>,
    columns: List<TableColumnDef<T>>,
    emptyMessage: String = "Geen items gevonden.",
    expandedRowKey: String? = null,
    getRowKey: ((T) -> String)? = null,
    onRowClick: ((T) -> Unit)? = null,
    expandedContent: (@Composable (T) -> Unit)? = null,
    groupKeyOf: ((T) -> String)? = null,
    groupOrder: List<String>? = null
) {
    // Validate: expansion features require getRowKey
    require(expandedRowKey == null || getRowKey != null) {
        "getRowKey must be provided when expandedRowKey is used for row expansion"
    }
    require(expandedContent == null || getRowKey != null) {
        "getRowKey must be provided when expandedContent is used for row expansion"
    }

    var sortColumnIndex by remember { mutableStateOf<Int?>(null) }
    var sortAscending by remember { mutableStateOf(true) }

    val sortedItems = remember(items, columns, sortColumnIndex, sortAscending) {
        val col = sortColumnIndex
        if (col == null || col >= columns.size) {
            items
        } else {
            val comparator = columns[col].comparator ?: return@remember items
            if (sortAscending) items.sortedWith(comparator) else items.sortedWith(comparator.reversed())
        }
    }

    // When grouping is requested, split the (already sorted) items into labeled buckets, preserving
    // sort order within each bucket, and ordering the buckets themselves via groupOrder (or first
    // appearance if not provided). A single null-labeled bucket is used when no grouping applies.
    val groupedRows: List<Pair<String?, List<T>>> = remember(sortedItems, groupKeyOf, groupOrder) {
        if (groupKeyOf == null) {
            listOf(null to sortedItems)
        } else {
            val buckets = sortedItems.groupBy(groupKeyOf)
            val order = (groupOrder.orEmpty() + buckets.keys).distinct()
            order.mapNotNull { key -> buckets[key]?.let { key to it } }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(TableStyles.borderRadius)
            .overflow(Overflow.Auto)
    ) {
        Table(
            attrs = Modifier
                .fillMaxWidth()
                .backgroundColor(MaterialColorVars.SurfaceContainer.value())
                .borderCollapse(BorderCollapse.Collapse)
                .toAttrs()
        ) {
            Thead {
                Tr(attrs = Modifier.backgroundColor(MaterialColorVars.SurfaceVariant.value()).toAttrs()) {
                    columns.forEachIndexed { index, col ->
                        val isSorted = sortColumnIndex == index
                        val indicator = when {
                            col.comparator == null -> ""
                            isSorted && sortAscending -> " ↑"
                            isSorted -> " ↓"
                            else -> ""
                        }
                        Th(
                            attrs = Modifier
                                .padding(
                                    leftRight = TableStyles.headerPaddingLeftRight,
                                    topBottom = TableStyles.headerPaddingTopBottom
                                )
                                .fontWeight(FontWeight.SemiBold)
                                .fontSize(14.px)
                                .color(if (isSorted) MaterialColorVars.Primary.value() else MaterialColorVars.OnSurfaceVariant.value())
                                .textAlign(TextAlign.Left)
                                .thenIf(col.wrapContent) {
                                    Modifier.styleModifier {
                                        property("white-space", "nowrap")
                                        property("width", "1%")
                                    }
                                }
                                .thenIf(col.resizable) {
                                    Modifier
                                        .resize(Resize.Horizontal)
                                        .overflow(Overflow.Hidden)
                                        .minWidth(TableStyles.minColumnWidth)
                                }
                                .thenIf(col.comparator != null) {
                                    Modifier
                                        .cursor(Cursor.Pointer)
                                        .userSelect(UserSelect.None)
                                }
                                .then(
                                    if (col.comparator != null) Modifier.onClick {
                                        when {
                                            // Not sorted yet -> sort ascending
                                            !isSorted -> {
                                                sortColumnIndex = index
                                                sortAscending = true
                                            }
                                            // Currently ascending -> sort descending
                                            sortAscending -> sortAscending = false
                                            // Currently descending -> reset to original order
                                            else -> sortColumnIndex = null
                                        }
                                    } else Modifier
                                )
                                .toAttrs()
                        ) { Text(col.heading + indicator) }
                    }
                }
            }
            Tbody {
                if (items.isEmpty()) {
                    // Show single cell spanning all columns
                    Tr(
                        attrs = Modifier
                            .backgroundColor(MaterialColorVars.SurfaceContainer.value())
                            .styleModifier {
                                borderTop(TableStyles.borderWidth, LineStyle.Solid, MaterialColorVars.OutlineVariant.value())
                            }
                            .toAttrs()
                    ) {
                        Td(
                            attrs = Modifier
                                .padding(
                                    leftRight = TableStyles.cellPaddingLeftRight,
                                    topBottom = 20.px
                                )
                                .fontSize(14.px)
                                .color(MaterialColorVars.OnSurfaceVariant.value())
                                .height(TableStyles.emptyStateHeight)
                                .textAlign(TextAlign.Center)
                                .verticalAlign(VerticalAlign.Middle)
                                .toAttrs {
                                    attr("colspan", columns.size.toString())
                                }
                        ) {
                            Text(emptyMessage)
                        }
                    }
                } else {
                    var rowIndex = 0
                    groupedRows.forEachIndexed { groupIndex, (groupLabel, groupItems) ->
                        if (groupLabel != null) {
                            Tr(
                                attrs = Modifier
                                    .backgroundColor(MaterialColorVars.SurfaceContainerHighest.value())
                                    .styleModifier {
                                        borderTop(
                                            TableStyles.borderWidth,
                                            LineStyle.Solid,
                                            MaterialColorVars.OutlineVariant.value()
                                        )
                                        if (groupIndex > 0) property("border-top-width", "2px")
                                    }
                                    .toAttrs()
                            ) {
                                Th(
                                    attrs = Modifier
                                        .padding(
                                            leftRight = TableStyles.cellPaddingLeftRight,
                                            topBottom = TableStyles.headerPaddingTopBottom
                                        )
                                        .fontWeight(FontWeight.Bold)
                                        .fontSize(12.px)
                                        .color(MaterialColorVars.Primary.value())
                                        .textAlign(TextAlign.Left)
                                        .styleModifier {
                                            property("text-transform", "uppercase")
                                            property("letter-spacing", "0.06em")
                                        }
                                        .toAttrs {
                                            attr("colspan", columns.size.toString())
                                            attr("scope", "rowgroup")
                                        }
                                ) {
                                    Text(groupLabel)
                                }
                            }
                        }

                        groupItems.forEach { item ->
                            val index = rowIndex++
                            val rowKey = getRowKey?.invoke(item)
                            val isExpanded = expandedRowKey != null && rowKey == expandedRowKey

                            // Main row
                            Tr(
                                attrs = Modifier
                                    .backgroundColor(
                                        if (isExpanded) MaterialColorVars.PrimaryContainer.value()
                                        else if (index % 2 == 0) MaterialColorVars.SurfaceContainer.value()
                                        else MaterialColorVars.SurfaceContainerHigh.value()
                                    )
                                    .styleModifier {
                                        borderTop(TableStyles.borderWidth, LineStyle.Solid, MaterialColorVars.OutlineVariant.value())
                                    }
                                    .thenIf(onRowClick != null) {
                                        Modifier
                                            .cursor(Cursor.Pointer)
                                            .onClick { onRowClick?.invoke(item) }
                                    }
                                    .toAttrs()
                            ) {
                                columns.forEachIndexed { colIndex, col ->
                                    Td(
                                        attrs = Modifier
                                            .padding(
                                                leftRight = TableStyles.cellPaddingLeftRight,
                                                topBottom = TableStyles.cellPaddingTopBottom
                                            )
                                            .thenIf(groupKeyOf != null && colIndex == 0) {
                                                Modifier.styleModifier {
                                                    property("padding-left", "32px")
                                                }
                                            }
                                            .fontSize(14.px)
                                            .color(
                                                if (isExpanded) MaterialColorVars.OnPrimaryContainer.value()
                                                else MaterialColorVars.OnSurfaceVariant.value()
                                            )
                                            .thenIf(col.wrapContent) {
                                                Modifier.styleModifier {
                                                    property("white-space", "nowrap")
                                                    property("width", "1%")
                                                }
                                            }
                                            .toAttrs()
                                    ) {
                                        col.cellContent(item)
                                    }
                                }
                            }

                            // Expanded row (if enabled)
                            if (isExpanded && expandedContent != null) {
                                Tr(
                                    attrs = Modifier
                                        .backgroundColor(MaterialColorVars.SurfaceContainerLow.value())
                                        .toAttrs()
                                ) {
                                    Td(
                                        attrs = Modifier
                                            .padding(TableStyles.expandedContentPadding)
                                            .toAttrs {
                                                attr("colspan", columns.size.toString())
                                            }
                                    ) {
                                        expandedContent(item)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

